import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText, LoadingIndicator, Row } from "@/components";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useCreateAddUser } from "@/hooks/createAdds/useCreateAddUser";
import { useUpdateSetUser } from "@/hooks/updateSets/useUpdateSetUser";
import {
  accessRoleOptions,
  accessRoleToLabel,
  userRoleOptions,
  userRoleToLabel,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { Role, User, UserRole } from "@/types";

const userFormSchema = z.object({
  firstName: z.string().min(1, "Required"),
  lastName: z.string().min(1, "Required"),
  email: z.string().min(1, "Required").email("Invalid email"),
  role: z.nativeEnum(Role),
  userRole: z.nativeEnum(UserRole),
});

type UserFormData = z.infer<typeof userFormSchema>;

export const UserFormDialog = ({
  isOpen,
  setIsOpen,
  user,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  user?: User;
}) => {
  /* Database CRUD Operations */
  // Create
  const {
    mutate: createUser,
    isPending: isCreatePending,
    isError: isCreateError,
    reset: resetCreate,
  } = useCreateAddUser();
  // Update
  const {
    mutate: updateUser,
    isPending: isUpdatePending,
    isError: isUpdateError,
    reset: resetUpdate,
  } = useUpdateSetUser();

  /* React Hooks */
  const isEditing = !!user;
  const form = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      email: user?.email ?? "",
      role: user?.role ?? Role.STAFFER,
      userRole: user?.userRole ?? UserRole.STAFFER,
    },
  });

  // Sync the form when a different user is selected for editing.
  useEffect(() => {
    form.reset({
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      email: user?.email ?? "",
      role: user?.role ?? Role.STAFFER,
      userRole: user?.userRole ?? UserRole.STAFFER,
    });
  }, [user, form]);

  /* Handlers */
  const handleSubmit = (data: UserFormData) => {
    mixpanelUtils.trackFormSubmission(
      isEditing ? "Update user" : "Create user",
      MIXPANEL_SCREENS.USERS,
    );

    if (isEditing) {
      updateUser(
        { id: user.id, updateInternalUserRequest: data },
        {
          onSuccess: () => {
            setIsOpen(false);
          },
        },
      );
      return;
    }

    createUser(
      { createInternalUserRequest: data },
      {
        onSuccess: () => {
          form.reset();
          setIsOpen(false);
        },
      },
    );
  };

  /* Render */
  const isPending = isCreatePending || isUpdatePending;
  const isError = isCreateError || isUpdateError;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        form.reset();
        resetCreate();
        resetUpdate();
        setIsOpen(open);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <Column className="gap-1">
          <p className="text-2xl font-semibold text-slate-700">
            {isEditing ? "Edit user" : "Add a user"}
          </p>
          <p className="text-sm text-slate-700">
            {isEditing
              ? "Update this user's details and role."
              : "Create a new internal user and assign their role."}
          </p>
        </Column>
        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <Column className="gap-4">
              <Row className="gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem className="flex-1 text-slate-700">
                      <FormLabel>First name</FormLabel>
                      <FormControl>
                        <Input placeholder="First name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem className="flex-1 text-slate-700">
                      <FormLabel>Last name</FormLabel>
                      <FormControl>
                        <Input placeholder="Last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Row>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Email" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Row className="gap-4">
                <FormField
                  control={form.control}
                  name="userRole"
                  render={({ field }) => (
                    <FormItem className="flex-1 text-slate-700">
                      <FormLabel>User role</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a user role" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {userRoleOptions.map((userRole) => (
                            <SelectItem key={userRole} value={userRole}>
                              {userRoleToLabel[userRole]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem className="flex-1 text-slate-700">
                      <FormLabel>Access role</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select an access role" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {accessRoleOptions.map((role) => (
                            <SelectItem key={role} value={role}>
                              {accessRoleToLabel[role]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Row>
            </Column>
            <Column className="gap-2">
              <Row className="items-center justify-end gap-2">
                <Button
                  className="rounded-full"
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                  }}
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button
                  className="relative rounded-full"
                  disabled={isPending}
                  type="submit"
                >
                  <span className={isPending ? "invisible" : ""}>
                    {isEditing ? "Save changes" : "Add user"}
                  </span>
                  {isPending && (
                    <LoadingIndicator
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                      gearColor="#fff"
                      variant="small"
                    />
                  )}
                </Button>
              </Row>
              {isError && (
                <ErrorText errorMessage="Something went wrong. Please try again." />
              )}
            </Column>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
